import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, field, IntegerDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { v4 as uuidv4 } from "uuid";
import { Group } from './Group';
// Eslint wants to remove Permissions, but it is needed for types!
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { PermissionLevel, PermissionRole, PermissionsByRole } from './Permissions';
/**
 * Give access to a given resouce based by the roles of a user
 */
export class GroupCategoryPermissions extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * Can create new groups in this category or subcategories
         */
        this.create = [];
        /**
         * Permissions automatically for all groups in this category
         */
        this.groupPermissions = PermissionsByRole.create({});
    }
    /**
     * Whetever a given user has access to the members in this group.
     */
    getCreatePermissionLevel(permissions, allRoles) {
        if (permissions.hasFullAccess(allRoles)) {
            return "Create";
        }
        for (const role of this.create) {
            if (permissions.roles.find(r => r.id === role.id)) {
                return "Create";
            }
        }
        return PermissionLevel.None;
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(PermissionRole) })
], GroupCategoryPermissions.prototype, "create", void 0);
__decorate([
    field({ decoder: PermissionsByRole, version: 203, optional: true })
], GroupCategoryPermissions.prototype, "groupPermissions", void 0);
export class GroupCategorySettings extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.name = "";
        /**
         * Small text below the category title
         */
        this.description = "";
        this.public = true;
        this.maximumRegistrations = null;
        /// Might move these to private settings, but is not an issue atm
        this.permissions = GroupCategoryPermissions.create({});
    }
}
__decorate([
    field({ decoder: StringDecoder })
], GroupCategorySettings.prototype, "name", void 0);
__decorate([
    field({ decoder: StringDecoder })
], GroupCategorySettings.prototype, "description", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], GroupCategorySettings.prototype, "public", void 0);
__decorate([
    field({ decoder: IntegerDecoder, nullable: true, version: 59 })
], GroupCategorySettings.prototype, "maximumRegistrations", void 0);
__decorate([
    field({ decoder: GroupCategoryPermissions, version: 61 })
], GroupCategorySettings.prototype, "permissions", void 0);
export class GroupCategory extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.settings = GroupCategorySettings.create({});
        /**
         * We only save the ID of groups because these are stored separately. To prevent that a group will be deleted when we simply move the group to a different category
         */
        this.groupIds = [];
        /**
         * Child category IDS
         * We use ID's to allow more flexible changes in the future
         */
        this.categoryIds = [];
    }
    /**
     * Returns all parent and grandparents of this group
     */
    getParentCategories(all) {
        const map = new Map();
        // Avoid recursive loop: can never call getParentCategories on itself again
        const filteredAll = all.filter(g => g.id !== this.id);
        const parents = filteredAll.filter(g => g.categoryIds.includes(this.id));
        for (const parent of parents) {
            map.set(parent.id, parent);
            const hisParents = parent.getParentCategories(filteredAll);
            for (const pp of hisParents) {
                map.set(pp.id, pp);
            }
        }
        return [...map.values()];
    }
    isPublic(allCategories) {
        if (!this.settings.public) {
            return false;
        }
        for (const parent of this.getParentCategories(allCategories)) {
            if (!parent.settings.public) {
                return false;
            }
        }
        return true;
    }
    canEdit(permissions, allRoles) {
        if (permissions.hasFullAccess(allRoles)) {
            return true;
        }
        return false;
    }
    canCreate(permissions, categories = [], allRoles) {
        if (permissions.hasFullAccess(allRoles)) {
            return true;
        }
        for (const role of this.settings.permissions.create) {
            if (permissions.roles.find(r => r.id === role.id)) {
                // Check role exists
                if (!allRoles.find(r => r.id === role.id)) {
                    continue;
                }
                return true;
            }
        }
        const parents = this.getParentCategories(categories);
        for (const parent of parents) {
            if (parent.canCreate(permissions, [], allRoles)) {
                return true;
            }
        }
        return false;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], GroupCategory.prototype, "id", void 0);
__decorate([
    field({ decoder: GroupCategorySettings })
], GroupCategory.prototype, "settings", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder) })
], GroupCategory.prototype, "groupIds", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder) })
], GroupCategory.prototype, "categoryIds", void 0);
export class GroupCategoryTree extends GroupCategory {
    constructor() {
        super(...arguments);
        this.groups = [];
        this.categories = [];
    }
    get depth() {
        if (this.groups.length > 0) {
            return 0;
        }
        if (this.categories.length == 0) {
            return 0;
        }
        return Math.max(...this.categories.map(c => c.depth)) + 1;
    }
    getAllGroups() {
        return [...this.groups, ...this.categories.flatMap(c => c.getAllGroups())];
    }
    getAllCategories() {
        return [...this.categories, ...this.categories.flatMap(c => c.getAllCategories())];
    }
    getMemberCount({ cycleOffset, waitingList } = {}) {
        if (this.getAllGroups().length == 0) {
            return null;
        }
        let count = 0;
        for (const group of this.getAllGroups()) {
            const c = group.getMemberCount({ cycleOffset, waitingList });
            if (c === null) {
                if (cycleOffset && group.cycle < cycleOffset) {
                    // This group did not have active registrations at the time
                    continue;
                }
                return null;
            }
            count += c;
        }
        return count;
    }
    static build(root, organization, options = {}) {
        var _a, _b, _c, _d;
        const categories = organization.meta.categories;
        const groups = (_a = options === null || options === void 0 ? void 0 : options.groups) !== null && _a !== void 0 ? _a : organization.groups;
        const permissions = (_b = options.permissions) !== null && _b !== void 0 ? _b : null;
        const maxDepth = (_c = options.maxDepth) !== null && _c !== void 0 ? _c : null;
        const smartCombine = (_d = options.smartCombine) !== null && _d !== void 0 ? _d : false;
        return GroupCategoryTree.create({
            ...root,
            categories: root.categoryIds.flatMap(id => {
                var _a, _b;
                const f = categories.find(c => c.id === id);
                if (f) {
                    const t = GroupCategoryTree.build(f, organization, {
                        ...options,
                        maxDepth: maxDepth !== null ? maxDepth - 1 : null
                    });
                    if (t.categories.length == 0 && t.groups.length == 0 && (smartCombine || (permissions !== null && !f.canCreate(permissions, categories, (_b = (_a = organization === null || organization === void 0 ? void 0 : organization.privateMeta) === null || _a === void 0 ? void 0 : _a.roles) !== null && _b !== void 0 ? _b : [])))) {
                        // Hide empty categories where we cannot create new groups or when smart combine is enabled
                        return [];
                    }
                    // if (smartCombine && !t.categories.find(c => c.categories.length || c.groups.length > 1)) {
                    //     // If all categories only have groups and no more than 1 group, combine them all
                    //     t.groups = t.getAllGroups()
                    //     t.categories = []
                    // }
                    if (maxDepth !== null && t.depth >= maxDepth && t.categories.length > 0) {
                        const categories = [];
                        for (const cat of t.categories) {
                            if (smartCombine && cat.groups.length === 0 && cat.categories.length === 0) {
                                continue;
                            }
                            // Clone reference
                            cat.settings = GroupCategorySettings.create(cat.settings);
                            cat.settings.name = t.settings.name + " - " + cat.settings.name;
                            cat.settings.public = t.settings.public && cat.settings.public;
                            categories.push(cat);
                        }
                        // Concat here
                        return categories;
                    }
                    return [t];
                }
                return [];
            }),
            groups: root.groupIds.flatMap(id => {
                const g = groups.find(c => c.id === id);
                if (g) {
                    // Hide groups we don't have permissions for
                    if (permissions && (!organization || !g.hasReadAccess(permissions, organization))) {
                        return [];
                    }
                    return [g];
                }
                return [];
            })
        });
    }
    /**
     * Filter groups
     */
    filter(keep) {
        const categories = this.categories.flatMap((category) => {
            const filtered = category.filter(keep);
            if (filtered.groups.length == 0 && filtered.categories.length == 0) {
                return [];
            }
            return [filtered];
        });
        const groups = this.groups.filter(keep);
        return GroupCategoryTree.create(Object.assign({}, this, {
            categories,
            groups
        }));
    }
    /**
     * Remove empty categories and non-public categories
     * @param admin Whether not-public categories should be visible
     */
    filterForDisplay(admin = false, useActivities = true, smartCombine = false) {
        const categories = this.categories.flatMap((category) => {
            if (!admin && !category.settings.public) {
                return [];
            }
            const filtered = category.filterForDisplay(admin, useActivities);
            if (smartCombine && filtered.groups.length == 0 && filtered.categories.length == 0) {
                return [];
            }
            return [filtered];
        });
        return GroupCategoryTree.create(Object.assign({}, this, {
            categories: (useActivities || categories.length == 0) ? categories : [categories[0]]
        }));
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(Group) })
], GroupCategoryTree.prototype, "groups", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(GroupCategoryTree) })
], GroupCategoryTree.prototype, "categories", void 0);
//# sourceMappingURL=GroupCategory.js.map